import { useState } from "react";
import { Sparkles, Image as ImageIcon, X, Sun, CloudSun, Moon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { format } from "date-fns";
import axios, { API } from "@/utils/api";
import RichTextEditor from "@/components/RichTextEditor";
import { DailyPrompt } from "@/components/DailyPromptAffirmation";
import { MOOD_EMOJIS } from "@/utils/constants";
import MoodTracker from "./MoodTracker";
import ImageUploader from "./ImageUploader";
import { useFeatures, FeatureGate } from "@/context/FeaturesContext";

const DiaryEditor = ({ entry, onSave, onCancel, userName }) => {
  const { customMood } = useFeatures();
  const [title, setTitle] = useState(entry?.title || "");
  const [content, setContent] = useState(entry?.content || "");
  const [mood, setMood] = useState(
    entry?.mood
      ? MOOD_EMOJIS.find((m) => m.value === entry.mood) || null
      : null
  );
  const [images, setImages] = useState(entry?.images || []);
  const [localImages, setLocalImages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [currentEntryId, setCurrentEntryId] = useState(entry?.id || null);

  // Get first name from full name
  const firstName = userName?.split(' ')[0] || 'there';

  // Get time-based greeting using user's local timezone
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 0 && hour < 12) {
      // 12:00 AM to 11:59 AM
      return { text: 'Good Morning', Icon: Sun };
    } else if (hour >= 12 && hour < 18) {
      // 12:00 PM to 5:59 PM
      return { text: 'Good Afternoon', Icon: CloudSun };
    } else {
      // 6:00 PM to 11:59 PM
      return { text: 'Good Evening', Icon: Moon };
    }
  };

  const greeting = getGreeting();

  const handleImageUpload = (imageData) => {
    setImages([...images, imageData.id]);
  };

  const handleLocalImageAdd = (imageData) => {
    setLocalImages([...localImages, imageData]);
  };

  const handleRemoveLocalImage = (index) => {
    const img = localImages[index];
    if (img.localUrl) {
      URL.revokeObjectURL(img.localUrl);
    }
    setLocalImages(localImages.filter((_, i) => i !== index));
  };

  const uploadPendingImages = async (entryId) => {
    for (const img of localImages) {
      const formData = new FormData();
      formData.append("file", img.file);
      try {
        const response = await axios.post(
          `${API}/entries/${entryId}/images`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        setImages(prev => [...prev, response.data.id]);
      } catch (error) {
        console.error("Failed to upload image:", error);
      }
    }
    localImages.forEach(img => {
      if (img.localUrl) URL.revokeObjectURL(img.localUrl);
    });
    setLocalImages([]);
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error("Please give your entry a title!");
      return;
    }
    
    // Strip HTML tags to check if there's actual text content
    const textContent = content.replace(/<[^>]*>/g, '').trim();
    if (!textContent) {
      toast.error("Please write something in your journal!");
      return;
    }
    if (!mood) {
      toast.error("Please select your mood!");
      return;
    }

    setSaving(true);
    try {
      // Use local date to ensure entry is saved for the correct day in user's timezone
      const today = format(new Date(), "yyyy-MM-dd");
      const entryData = {
        title: title.trim() || null,
        content: content,  // Keep the HTML content as-is
        mood: mood.value,
        links: [],
        date: today,
      };

      let savedEntry;
      if (currentEntryId) {
        const response = await axios.put(
          `${API}/entries/${currentEntryId}`,
          entryData
        );
        savedEntry = response.data;
        if (localImages.length > 0) {
          await uploadPendingImages(currentEntryId);
        }
        toast.success("Entry updated! ✨");
      } else {
        const response = await axios.post(`${API}/entries`, entryData);
        savedEntry = response.data;
        setCurrentEntryId(savedEntry.id);
        if (localImages.length > 0) {
          await uploadPendingImages(savedEntry.id);
        }
        toast.success("Entry saved! ✨");
      }

      onSave(savedEntry);
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save entry");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="diary-editor">
      <div className="glass-card p-4 sm:p-6">
        <div className="text-center mb-4 sm:mb-6">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold gradient-text mb-3 flex items-center justify-center gap-1 sm:gap-2">
            <greeting.Icon className="w-5 h-5 sm:w-7 sm:h-7 md:w-8 md:h-8 flex-shrink-0" style={{ color: 'var(--theme-primary)' }} />
            <span>{greeting.text}, {firstName}!</span>
          </h2>
          <span className="date-badge text-sm sm:text-base px-3 sm:px-5 py-1.5 sm:py-2">{format(new Date(), "MMMM d, yyyy")}</span>
        </div>
        
        {/* Title Input */}
        <div className="mb-4">
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Give your entry a title..."
            className="diary-input text-2xl sm:text-3xl font-semibold bg-transparent border-0 border-b-2 border-[var(--theme-border)]/30 focus:border-[var(--theme-primary)] focus-visible:ring-0 focus-visible:border-[var(--theme-primary)] rounded-none px-0 text-center h-16 py-4 placeholder:text-[var(--theme-text-secondary)]"
            style={{ color: 'var(--theme-text)' }}
            data-testid="entry-title-input"
          />
        </div>
        
        {/* Daily Prompt - only show for new entries */}
        {!entry && (
          <DailyPrompt 
            onUsePrompt={(prompt) => {
              if (!content.trim()) {
                setContent(`<p>${prompt}</p><p></p>`);
              } else {
                setContent(content + `<p>${prompt}</p>`);
              }
            }} 
          />
        )}
        
        <RichTextEditor
          content={content}
          onChange={setContent}
          placeholder="What's on your mind? ✨"
        />
      </div>

      <MoodTracker selectedMood={mood} onMoodSelect={setMood} customMood={customMood} />

      <FeatureGate feature="image_attachments">
        <div className="glass-card p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <ImageIcon className="w-5 h-5" style={{ color: 'var(--theme-primary)' }} />
            <span className="text-white">Add Photos</span>
          </h3>
          <ImageUploader 
            entryId={currentEntryId} 
            onUploadComplete={handleImageUpload}
            onLocalImageAdd={handleLocalImageAdd}
          />
          {localImages.length > 0 && (
            <div className="image-gallery mt-4">
              {localImages.map((img, index) => (
                <div key={img.localUrl || `local-img-${index}`} className="relative group">
                  <img
                    src={img.localUrl}
                    alt="Pending upload"
                    className="gallery-image"
                  />
                  <button
                    onClick={() => handleRemoveLocalImage(index)}
                    className="absolute top-2 right-2 p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ backgroundColor: 'var(--theme-background)' }}
                  >
                    <X className="w-4 h-4" style={{ color: 'var(--theme-primary)' }} />
                  </button>
                  <div className="absolute bottom-2 left-2 text-xs text-white px-2 py-1 rounded-full" style={{ backgroundColor: 'var(--theme-secondary)' }}>
                    Pending
                  </div>
                </div>
              ))}
            </div>
          )}
          {images.length > 0 && (
            <div className="image-gallery mt-4">
              {images.map((imageId) => (
                <img
                  key={imageId}
                  src={`${API}/images/${imageId}`}
                  alt="Journal"
                  className="gallery-image"
                />
              ))}
            </div>
          )}
        </div>
      </FeatureGate>

      {/* Spacer for footer clearance */}
      <div className="pb-2"></div>

      {/* Save/Cancel buttons - normal flow, not sticky */}
      <div className="flex flex-col sm:flex-row gap-4 justify-center items-center py-4">
        {onCancel && (
          <Button
            onClick={onCancel}
            className="btn-secondary w-full sm:w-auto px-12 py-4 text-lg font-semibold"
            data-testid="cancel-btn"
          >
            Cancel
          </Button>
        )}
        <Button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary flex items-center justify-center gap-2 w-full sm:w-auto px-12 py-4 text-lg font-semibold"
          data-testid="save-entry-btn"
        >
          {saving ? (
            <>
              <div className="loading-spinner w-5 h-5" />
              Saving...
            </>
          ) : (
            <>
              <Sparkles className="w-5 h-5" />
              Save Entry
            </>
          )}
        </Button>
      </div>

      {/* Bottom padding for footer clearance */}
      <div className="pb-12 sm:pb-6"></div>
    </div>
  );
};

export default DiaryEditor;
